class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      
      // Explicitly trigger the Lottie animation when opening the drawer
      const lottie = this.querySelector('dotlottie-wc');
      if (lottie) {
        // dotlottie-wc auto-plays on src set; force restart by toggling src
        const src = lottie.getAttribute('src');
        if (src) {
          lottie.removeAttribute('src');
          setTimeout(() => {
            lottie.setAttribute('src', src);
          }, 10);
        }
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      
      this.clearGokwikLoading();
      setTimeout(() => this.clearGokwikLoading(), 300);
      setTimeout(() => this.clearGokwikLoading(), 800);
      setTimeout(() => this.clearGokwikLoading(), 1500);

      const lottie = this.querySelector('dotlottie-wc');
      if (lottie) {
        const src = lottie.getAttribute('src');
        if (src) {
          lottie.removeAttribute('src');
          setTimeout(() => lottie.setAttribute('src', src), 10);
        }
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /** Show the full-drawer overlay loader */
  showLoader() {
    const overlay = document.getElementById('CartDrawer-LoaderOverlay');
    if (overlay) overlay.classList.add('active');
  }

  /**
   * Hide the full-drawer overlay loader and clear GoKwik's button state.
   *
   * The GoKwik checkout button is rendered born `disabled` with an inline
   * #ffbf00 background, so it must be cleared after every DOM swap or it shows
   * as a dead yellow block.
   */
  hideLoader() {
    this.clearGokwikLoading();
    const overlay = document.getElementById('CartDrawer-LoaderOverlay');
    if (overlay) overlay.classList.remove('active');
  }

  /** Force-clear GoKwik checkout button's own loading state */
  clearGokwikLoading() {
    // GoKwik's loader divs (contains .cir-loader spinner) - there may be multiple
    document.querySelectorAll('#btn-loader').forEach((loader) => {
      loader.style.display = 'none';
      loader.classList.remove('active');
    });
    // GoKwik checkout buttons — remove loading states across all instances
    document.querySelectorAll('.gokwik-checkout button').forEach((btn) => {
      btn.classList.remove('loading', 'disabled');
      btn.removeAttribute('disabled');
      btn.style.background = '';
      btn.style.backgroundColor = '';
    });
    // Also clear any .loading class on .button elements inside drawer footer
    document.querySelectorAll('.drawer__footer .button.loading').forEach((btn) => {
      btn.classList.remove('loading');
    });
  }

}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * Override: Show the full-drawer overlay loader instead of per-item spinners.
   * For removals (quantity=0), also animate the item card out smoothly.
   */
  updateQuantity(line, quantity, event, name, variantId) {
    this.cartOperationId = (this.cartOperationId || 0) + 1;
    const operationId = this.cartOperationId;
    if (this.cartAbortController) this.cartAbortController.abort();
    this.cartAbortController = new AbortController();

    const isRemoval = quantity === 0;
    const cartDrawer = document.querySelector('cart-drawer');
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    // Show full overlay loader
    if (cartDrawer) cartDrawer.showLoader();
    this.classList.add('is-loading');

    // Smooth slide-out animation for removed item
    const removingEl = isRemoval
      ? (document.getElementById(`CartDrawer-Item-${line}`))
      : null;
    if (removingEl) {
      removingEl.style.maxHeight = removingEl.offsetHeight + 'px';
      // Force reflow before adding class
      removingEl.offsetHeight;
      removingEl.classList.add('is-removing');
    }

    const restoreRemovingItem = () => {
      if (!removingEl) return;
      removingEl.classList.remove('is-removing');
      removingEl.style.maxHeight = '';
    };

    const body = JSON.stringify({
      id: line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body }, signal: this.cartAbortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.text();
      })
      .then((state) => {
        if (operationId !== this.cartOperationId) return;
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          restoreRemovingItem();
          return;
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const sectionEl = document.getElementById(section.id);
            if (!sectionEl) return;
            const elementToReplace =
              sectionEl.querySelector(section.selector) || sectionEl;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement?.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (cartDrawerWrapper?.querySelector('.cart-item__name')) {
            if (event.target === this) {
              trapFocus(cartDrawerWrapper, cartDrawerWrapper.querySelector('.cart-item__name'));
            }
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { data: { itemCount: parsedState.item_count } }
        }));
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        restoreRemovingItem();
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (operationId !== this.cartOperationId) return;
        if (cartDrawer) cartDrawer.hideLoader();
        this.classList.remove('is-loading');
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  /**
   * Override: Use full-drawer overlay instead of per-item spinners.
   */
  enableLoading(line) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.showLoader();
    this.classList.add('is-loading');
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.hideLoader();
    this.classList.remove('is-loading');
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
